import { message, Table } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CustomPagination from '../custom-pagination/CustomPagination';
import CustomSelect from '../custom-select/CustomSelect';
import {
  formatCabinetHistoryAmount,
  formatCabinetHistoryDateTime,
} from '../client-detail-drawer/cabinet-history-from-orders';
import { apiService } from '../../services/api';
import './balance-operations-logs.css';

const PAGE_SIZE_OPTIONS = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;

const ORDER_TYPE_FILTERS = [
  { value: 'cabinet_topup', label: 'Пополнение счёта' },
  { value: 'admin_manual_topup', label: 'Пополнение (Админ)' },
  { value: 'admin_manual_debit', label: 'Списание (Админ)' },
  { value: 'admin_manual_refund', label: 'Возврат (Админ)' },
  { value: 'provisioning_refund_credit', label: 'Возврат на баланс (прокси)' },
  { value: 'new_purchase', label: 'Списание (Покупка)' },
  { value: 'renewal', label: 'Списание (Продление)' },
  { value: 'balance_auto_renewal', label: 'Списание (Автопродление)' },
];

const DIRECTION_FILTERS = [
  { value: '', label: 'Все направления' },
  { value: 'credit', label: 'Пополнения / возвраты' },
  { value: 'debit', label: 'Списания' },
];

const EMPTY_FILTERS = {
  client_logins: [],
  order_types: [],
  direction: '',
  date_from: '',
  date_to: '',
};

const FilterFunnelIcon = ({ filtered }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ color: filtered ? '#5283f4' : '#7a7a7a' }}
    aria-hidden="true"
  >
    <path d="M9.00005 14C8.89187 14 8.7866 13.9649 8.70005 13.9L6.70005 12.4C6.63795 12.3534 6.58755 12.293 6.55284 12.2236C6.51812 12.1542 6.50005 12.0776 6.50005 12V9.19L2.99205 5.2435C2.74291 4.96244 2.58024 4.61541 2.52359 4.24412C2.46694 3.87283 2.51872 3.49308 2.67271 3.15051C2.8267 2.80794 3.07635 2.51713 3.39164 2.31303C3.70694 2.10894 4.07446 2.00024 4.45005 2H11.5501C11.9256 2.00044 12.293 2.10932 12.6082 2.31355C12.9234 2.51779 13.1729 2.80869 13.3267 3.15129C13.4805 3.4939 13.5321 3.87364 13.4753 4.24487C13.4185 4.61611 13.2558 4.96305 13.0066 5.244L9.50005 9.19V13.5C9.50005 13.6326 9.44737 13.7598 9.3536 13.8536C9.25984 13.9473 9.13266 14 9.00005 14ZM7.50005 11.75L8.50005 12.5V9C8.50015 8.87758 8.54516 8.75945 8.62655 8.668L12.2606 4.5795C12.3817 4.44254 12.4608 4.27352 12.4883 4.09273C12.5157 3.91193 12.4905 3.72705 12.4154 3.56028C12.3404 3.39351 12.2189 3.25193 12.0654 3.15254C11.9118 3.05316 11.7329 3.00019 11.5501 3H4.45005C4.26728 3.00028 4.08846 3.05327 3.93505 3.15263C3.78164 3.25199 3.66015 3.3935 3.58516 3.56018C3.51016 3.72685 3.48483 3.91163 3.51221 4.09234C3.5396 4.27305 3.61854 4.44203 3.73955 4.579L7.37405 8.668C7.45526 8.75952 7.50009 8.87764 7.50005 9V11.75Z" fill="currentColor" stroke="currentColor" strokeWidth="0.6"/>
  </svg>
);

const OpsFilterDropdown = ({
  title,
  filters,
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
}) => {
  const [search, setSearch] = useState('');
  const items = filters || [];
  const visibleItems = search
    ? items.filter((item) => String(item.text).toLowerCase().includes(search.toLowerCase()))
    : items;

  const handleToggle = (value) => {
    const next = selectedKeys.some((key) => String(key) === String(value))
      ? selectedKeys.filter((key) => String(key) !== String(value))
      : [...selectedKeys, value];
    setSelectedKeys(next);
  };

  const handleReset = () => {
    setSelectedKeys([]);
    if (clearFilters) clearFilters();
    confirm({ closeDropdown: true });
  };

  const handleApply = () => {
    confirm({ closeDropdown: true });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    }
  };

  return (
    <div className="bal-ops-filter">
      <div className="bal-ops-filter__header">Фильтр: {title}</div>
      <div className="bal-ops-filter__search">
        <input
          className="bal-ops-filter__search-input"
          placeholder="Поиск..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          aria-label={`Поиск в фильтре ${title}`}
          autoFocus
        />
      </div>
      <div className="bal-ops-filter__list">
        {visibleItems.length === 0 && (
          <div className="bal-ops-filter__empty">Ничего не найдено</div>
        )}
        {visibleItems.map((item) => (
          <label key={String(item.value)} className="bal-ops-filter__item">
            <input
              type="checkbox"
              className="bal-ops-filter__checkbox"
              checked={selectedKeys.some((key) => String(key) === String(item.value))}
              onChange={() => handleToggle(item.value)}
            />
            <span className="bal-ops-filter__label">{item.text}</span>
          </label>
        ))}
      </div>
      <div className="bal-ops-filter__actions">
        <button
          type="button"
          className="bal-ops-filter__btn bal-ops-filter__btn--reset"
          onClick={handleReset}
          aria-label={`Сбросить фильтр ${title}`}
        >
          Сбросить
        </button>
        <button
          type="button"
          className="bal-ops-filter__btn bal-ops-filter__btn--apply"
          onClick={handleApply}
          aria-label={`Применить фильтр ${title}`}
        >
          Применить
        </button>
      </div>
    </div>
  );
};

const badgeClassForRow = (row) => {
  if (row.is_refunded) {
    return 'sett-badge sett-badge--grey';
  }
  if (row.direction === 'credit') {
    return 'sett-badge sett-badge--green';
  }
  return 'sett-badge sett-badge--red';
};

const BalanceOperationsLogs = ({ refreshNonce = 0 }) => {
  const [items, setItems] = useState([]);
  const [loginOptions, setLoginOptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const fetchOperations = useCallback(async (nextPage, nextFilters, nextPageSize) => {
    setLoading(true);
    try {
      const params = {
        limit: nextPageSize,
        offset: (nextPage - 1) * nextPageSize,
      };
      if (Array.isArray(nextFilters.client_logins) && nextFilters.client_logins.length) {
        params.client_logins = nextFilters.client_logins.join(',');
      }
      if (Array.isArray(nextFilters.order_types) && nextFilters.order_types.length) {
        params.order_types = nextFilters.order_types.join(',');
      }
      if (nextFilters.direction) params.direction = nextFilters.direction;
      if (nextFilters.date_from) params.date_from = nextFilters.date_from;
      if (nextFilters.date_to) params.date_to = nextFilters.date_to;
      const res = await apiService.getAdminCabinetBalanceOperations(params);
      const data = res?.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total) || 0);
      setLoginOptions(Array.isArray(data.login_options) ? data.login_options : []);
    } catch (err) {
      message.error('Ошибка загрузки операций по балансу');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOperations(page, filters, pageSize);
  }, [fetchOperations, page, filters, pageSize, refreshNonce]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleTableChange = useCallback((_pagination, tableFilters) => {
    const nextLogins = Array.isArray(tableFilters?.client_login)
      ? tableFilters.client_login.filter(Boolean)
      : [];
    const nextTypes = Array.isArray(tableFilters?.order_type)
      ? tableFilters.order_type.filter(Boolean)
      : [];
    setFilters((prev) => ({
      ...prev,
      client_logins: nextLogins,
      order_types: nextTypes,
    }));
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const handlePerPageChange = useCallback((nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  }, []);

  const loginColumnFilters = useMemo(
    () => loginOptions.map((login) => ({ text: login, value: login })),
    [loginOptions],
  );

  const typeColumnFilters = useMemo(
    () => ORDER_TYPE_FILTERS.map((item) => ({ text: item.label, value: item.value })),
    [],
  );

  const columns = useMemo(() => [
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 168,
      render: (value) => (
        <span className="sett-cell--muted">{formatCabinetHistoryDateTime(value)}</span>
      ),
    },
    {
      title: 'Логин',
      dataIndex: 'client_login',
      key: 'client_login',
      width: 176,
      filters: loginColumnFilters,
      filteredValue: filters.client_logins.length ? filters.client_logins : null,
      filterMultiple: true,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => <OpsFilterDropdown title="Логин" {...props} />,
      onFilter: () => true,
      render: (value) => value || '—',
    },
    {
      title: 'Тип операции',
      dataIndex: 'type_label',
      key: 'order_type',
      filters: typeColumnFilters,
      filteredValue: filters.order_types.length ? filters.order_types : null,
      filterMultiple: true,
      filterIcon: (filtered) => <FilterFunnelIcon filtered={filtered} />,
      filterDropdown: (props) => <OpsFilterDropdown title="Тип операции" {...props} />,
      onFilter: () => true,
      render: (value, row) => (
        <div className="bal-ops-logs__type">
          <span className={badgeClassForRow(row)}>{value}</span>
          {row.is_refunded && row.refund_done_label ? (
            <span className="sett-badge sett-badge--grey">{row.refund_done_label}</span>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Сумма',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 140,
      align: 'right',
      render: (value, row) => {
        const sign = row.direction === 'credit' ? '+' : '-';
        return (
          <span className={`bal-ops-logs__sum${row.is_refunded ? ' bal-ops-logs__sum--refunded' : ''}`}>
            {`${sign}${formatCabinetHistoryAmount(value, row.currency)}`}
          </span>
        );
      },
    },
    {
      title: 'Заказ',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 160,
      render: (value) => value || '—',
    },
  ], [filters.client_logins, filters.order_types, loginColumnFilters, typeColumnFilters]);

  return (
    <div className="sett-content">
      <div className="sett-filters">
        <CustomSelect
          className="cs--filter"
          value={filters.direction}
          onChange={(val) => handleFilterChange('direction', val)}
          ariaLabel="Фильтр по направлению"
          placeholder="Все направления"
          options={DIRECTION_FILTERS}
        />
        <div className="sett-filter-date-row">
          <label className="sett-filter-date-wrap">
            <span className="sett-filter-date-label">Дата от</span>
            <input
              className="sett-filter-date"
              type="date"
              value={filters.date_from}
              onClick={(event) => { try { event.target.showPicker(); } catch (_) { /* native fallback */ } }}
              onChange={(event) => handleFilterChange('date_from', event.target.value)}
              aria-label="Дата от"
              title="Дата от"
            />
          </label>
          <label className="sett-filter-date-wrap">
            <span className="sett-filter-date-label">Дата до</span>
            <input
              className="sett-filter-date"
              type="date"
              value={filters.date_to}
              onClick={(event) => { try { event.target.showPicker(); } catch (_) { /* native fallback */ } }}
              onChange={(event) => handleFilterChange('date_to', event.target.value)}
              aria-label="Дата до"
              title="Дата до"
            />
          </label>
        </div>
      </div>

      <div className="sett-table">
        <Table
          dataSource={items}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          scroll={{ x: 920 }}
          onChange={handleTableChange}
          locale={{ emptyText: 'Операции по балансу появятся позже' }}
        />
        <CustomPagination
          currentPage={page}
          totalPages={Math.max(1, Math.ceil(total / pageSize))}
          totalItems={total}
          perPage={pageSize}
          perPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
        />
      </div>
    </div>
  );
};

export default BalanceOperationsLogs;
